import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Target, Globe, BarChart, ShieldCheck, Recycle, Zap } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Carbon Credit Solutions",
      description: "Helping businesses offset carbon emissions through trusted and verified carbon credit programs."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Net Zero Consultancy",
      description: "Providing strategic guidance and customized solutions to help organizations achieve their Net Zero goals."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Sustainability Consulting",
      description: "Supporting businesses in adopting environmentally responsible and sustainable practices for long-term growth."
    },
    {
      icon: <BarChart className="w-8 h-8" />,
      title: "Carbon Footprint Assessment",
      description: "Measuring and analyzing organizational carbon emissions to identify key reduction opportunities."
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "ESG & Climate Strategy",
      description: "Helping organizations align with Environmental, Social & Governance (ESG) goals and global sustainability standards."
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "Waste Management & Recycling",
      description: "Supporting efficient waste reduction, segregation, recycling, and responsible disposal practices to promote circular economy."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Renewable & Green Initiatives",
      description: "Promoting clean energy adoption, environmental projects, and sustainable development solutions for a greener future."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="services" className="py-24 bg-lightbg relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#14B8A6 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-primary font-bold tracking-wider uppercase mb-2">Our Services</h2>
          <h3 className="text-4xl font-extrabold text-teal-950 mb-6">Comprehensive Climate Solutions</h3>
          <p className="text-lg text-gray-700">
            CoxCred empowers businesses to turn climate responsibility into meaningful action through our expert services.
          </p>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <motion.div 
              key={index} 
              variants={itemVariants}
              className="glass-card p-8 group relative overflow-hidden"
            >
              {/* Subtle background glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              
              <div className="w-16 h-16 rounded-2xl bg-slate-50 text-emerald-600 flex items-center justify-center mb-6 group-hover:bg-teal-800 group-hover:text-white transition-all duration-500 shadow-sm relative z-10">
                {service.icon}
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 relative z-10">{service.title}</h4>
              <p className="text-slate-600 leading-relaxed relative z-10">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
